import { lazy } from "react"
import { BrowserRouter, Routes, Route } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { AuthProvider as OidcAuthProvider } from "@/auth/oidc"
import { MainLayout } from "@/components/layout"
import { AuthProvider } from "@/components/auth"
import { ProtectedRoute } from "@/components/auth/ProtectedRoute"
import { SetupGate } from "@/components/setup/SetupGate"
import { Login } from "@/pages/Login"
import { Callback } from "@/pages/Callback"
import { SetupPage } from "@/pages/SetupPage"
import { Toaster } from "@/components/ui/toaster"
import { Toaster as SonnerToaster } from "sonner"
// Lazy-loaded Pages (Code-Splitting)
const Dashboard = lazy(() => import("@/pages").then(m => ({ default: m.Dashboard })))
const OrganizationsList = lazy(() => import("@/pages").then(m => ({ default: m.OrganizationsList })))
const OrganizationDetail = lazy(() => import("@/pages").then(m => ({ default: m.OrganizationDetail })))
const ContactsList = lazy(() => import("@/pages").then(m => ({ default: m.ContactsList })))
const ContactDetail = lazy(() => import("@/pages").then(m => ({ default: m.ContactDetail })))
const MapView = lazy(() => import("@/pages").then(m => ({ default: m.MapView })))
const SearchPage = lazy(() => import("@/pages").then(m => ({ default: m.SearchPage })))
const AIImportPage = lazy(() => import("@/pages/AIImportPage").then(m => ({ default: m.AIImportPage })))
const ProjectbasePage = lazy(() => import("@/pages/ProjectbasePage").then(m => ({ default: m.ProjectbasePage })))
const ProjectbaseGuidePage = lazy(() => import("@/pages/ProjectbaseGuidePage").then(m => ({ default: m.ProjectbaseGuidePage })))
const JournalPage = lazy(() => import("@/pages/JournalPage").then(m => ({ default: m.JournalPage })))
const EmailSummariesPage = lazy(() => import("@/pages/EmailSummariesPage").then(m => ({ default: m.EmailSummariesPage })))
const OpenCamiPage = lazy(() => import("@/pages/OpenCamiPage").then(m => ({ default: m.OpenCamiPage })))
const RingPage = lazy(() => import("@/pages/RingPage").then(m => ({ default: m.RingPage })))
const ServerHealthTvPage = lazy(() => import("@/pages/ServerHealthTvPage"))
const MeetCallsTvPage = lazy(() => import("@/pages/MeetCallsTvPage"))

// Admin
const UsersListPage = lazy(() => import("@/pages/admin").then(m => ({ default: m.UsersListPage })))
const EmailSettingsPage = lazy(() => import("@/pages/admin").then(m => ({ default: m.EmailSettingsPage })))
const SettingsPage = lazy(() => import("@/pages/admin").then(m => ({ default: m.SettingsPage })))
const MatrixAdminPage = lazy(() => import("@/pages/admin").then(m => ({ default: m.MatrixAdminPage })))
const MattermostAdminPage = lazy(() => import("@/pages/admin").then(m => ({ default: m.MattermostAdminPage })))
const PagesPage = lazy(() => import("@/pages/admin").then(m => ({ default: m.PagesPage })))
const UserGroupsPage = lazy(() => import("@/pages/admin/UserGroupsPage").then(m => ({ default: m.UserGroupsPage })))
const TicketGroupsPage = lazy(() => import("@/pages/admin/TicketGroupsPage").then(m => ({ default: m.TicketGroupsPage })))

// DSGVO
const DsgvoDashboard = lazy(() => import("@/pages/dsgvo").then(m => ({ default: m.DsgvoDashboard })))
const DsgvoVendorsPage = lazy(() => import("@/pages/dsgvo").then(m => ({ default: m.VendorsPage })))
const DsgvoAVVPage = lazy(() => import("@/pages/dsgvo").then(m => ({ default: m.AVVPage })))
const DsgvoVVTPage = lazy(() => import("@/pages/dsgvo").then(m => ({ default: m.VVTPage })))
const DsgvoIncidentsPage = lazy(() => import("@/pages/dsgvo").then(m => ({ default: m.IncidentsPage })))
const DsgvoDSRPage = lazy(() => import("@/pages/dsgvo").then(m => ({ default: m.DSRPage })))
const DsgvoDocumentsPage = lazy(() => import("@/pages/dsgvo").then(m => ({ default: m.DocumentsPage })))
const DsgvoTasksPage = lazy(() => import("@/pages/dsgvo").then(m => ({ default: m.TasksPage })))

// Tickets
const TicketsDashboard = lazy(() => import("@/pages/tickets").then(m => ({ default: m.TicketsDashboard })))
const TicketDetailPage = lazy(() => import("@/pages/tickets").then(m => ({ default: m.TicketDetailPage })))

// Aufgaben (Forgejo-Issue-Boards)
const TaskBoardsPage = lazy(() => import("@/pages/tasks").then(m => ({ default: m.TaskBoardsPage })))
const TaskBoardPage = lazy(() => import("@/pages/tasks").then(m => ({ default: m.TaskBoardPage })))
const TesterwerkPage = lazy(() => import("@/pages/admin/TesterwerkPage").then(m => ({ default: m.TesterwerkPage })))
const TesterMeldungenPage = lazy(() => import("@/pages/TesterMeldungenPage").then(m => ({ default: m.TesterMeldungenPage })))

// Rechnungen
const RechnungenDashboard = lazy(() => import("@/pages/rechnungen").then(m => ({ default: m.RechnungenDashboard })))
const InvoicesPage = lazy(() => import("@/pages/rechnungen").then(m => ({ default: m.InvoicesPage })))
const InvoiceDetailPage = lazy(() => import("@/pages/rechnungen").then(m => ({ default: m.InvoiceDetailPage })))
const PaymentRunsPage = lazy(() => import("@/pages/rechnungen").then(m => ({ default: m.PaymentRunsPage })))
const PaymentRunDetailPage = lazy(() => import("@/pages/rechnungen").then(m => ({ default: m.PaymentRunDetailPage })))
const OCRImportPage = lazy(() => import("@/pages/rechnungen").then(m => ({ default: m.OCRImportPage })))
const LiquidityPage = lazy(() => import("@/pages/rechnungen").then(m => ({ default: m.LiquidityPage })))
const OutgoingInvoicesPage = lazy(() => import("@/pages/rechnungen").then(m => ({ default: m.OutgoingInvoicesPage })))
const OutgoingInvoiceDetailPage = lazy(() => import("@/pages/rechnungen").then(m => ({ default: m.OutgoingInvoiceDetailPage })))
const BankingPage = lazy(() => import("@/pages/rechnungen").then(m => ({ default: m.BankingPage })))

// HR
const HRDashboard = lazy(() => import("@/pages/hr").then(m => ({ default: m.HRDashboard })))
const EmployeesPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.EmployeesPage })))
const TimeTrackingPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.TimeTrackingPage })))
const VacationPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.VacationPage })))
const CalendarPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.CalendarPage })))
const WorkTimeModelsPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.WorkTimeModelsPage })))
const HRJournalPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.HRJournalPage })))
const PayoutsPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.PayoutsPage })))
const EmployeeCalendarPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.EmployeeCalendarPage })))
const MyHRPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.MyHRPage })))
const SickLeavePage = lazy(() => import("@/pages/hr").then(m => ({ default: m.SickLeavePage })))
const TeamOverviewPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.TeamOverviewPage })))
const ProjectTimePage = lazy(() => import("@/pages/hr").then(m => ({ default: m.ProjectTimePage })))
const ProjectsPage = lazy(() => import("@/pages/hr").then(m => ({ default: m.ProjectsPage })))


// OIDC-Konfiguration (Authentik) liegt jetzt im Adapter unter @/auth/oidc,
// gespeist aus der Runtime-Config (OIDC_ISSUER / OIDC_CLIENT_ID).

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

function MasterDataPage() {
  return (
    <div className="flex items-center justify-center h-96">
      <p className="text-muted-foreground">Stammdaten (in Entwicklung)</p>
    </div>
  )
}

function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center h-96 space-y-4">
      <h1 className="text-4xl font-bold">404</h1>
      <p className="text-muted-foreground">Seite nicht gefunden</p>
    </div>
  )
}

function RootEntry() {
  return (
    <ProtectedRoute>
      <MainLayout />
    </ProtectedRoute>
  )
}

function App() {
  return (
    <OidcAuthProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <SetupGate>
              <Routes>
                {/* Öffentliche Routen */}
                <Route path="/setup" element={<SetupPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/callback" element={<Callback />} />
                <Route path="/ring/:token" element={<RingPage />} />
                <Route path="/tv/kiosk/:code" element={<ServerHealthTvPage />} />
                <Route path="/tv/meet-calls" element={<MeetCallsTvPage />} />
                <Route path="/tv/server-health" element={
                  <ProtectedRoute role="admin">
                    <ServerHealthTvPage />
                  </ProtectedRoute>
                } />

                {/* Geschützte Routen */}
                <Route path="/" element={<RootEntry />}>
                {/* Startseite = Mein Arbeitsplatz (wenn HR aktiv, sonst Dashboard) */}
                <Route index element={<ProtectedRoute requiredModule="hr"><MyHRPage /></ProtectedRoute>} />
                <Route path="dashboard" element={<Dashboard />} />

                {/* Organizations */}
                <Route path="organizations" element={<OrganizationsList />} />
                <Route path="organizations/:id" element={<OrganizationDetail />} />

                {/* Contacts */}
                <Route path="contacts" element={<ContactsList />} />
                <Route path="contacts/:id" element={<ContactDetail />} />

                {/* Map */}
                <Route path="map" element={<MapView />} />

                {/* Ninox */}
                {/* Ninox-Routen entfernt: abgelöst durch nxtools (App-Switcher im Header) */}
                <Route path="projektbase" element={<ProtectedRoute requiredModule="projektbase"><ProjectbasePage /></ProtectedRoute>} />
                <Route path="projektbase/anleitung" element={<ProtectedRoute requiredModule="projektbase"><ProjectbaseGuidePage /></ProtectedRoute>} />
                <Route path="tagebuch" element={<ProtectedRoute requiredModule="projektbase"><JournalPage /></ProtectedRoute>} />

                {/* Search */}
                <Route path="search" element={<SearchPage />} />

                {/* E-Mail-Zusammenfassungen */}
                <Route path="email-summaries" element={<EmailSummariesPage />} />

                {/* AI Import */}
                <Route path="ai-import" element={<AIImportPage />} />

                {/* OpenCami */}
                <Route path="opencami" element={
                  <ProtectedRoute role="admin">
                    <OpenCamiPage />
                  </ProtectedRoute>
                } />

                {/* DSGVO */}
                <Route path="dsgvo" element={<ProtectedRoute requiredModule="dsgvo"><DsgvoDashboard /></ProtectedRoute>} />
                <Route path="dsgvo/vendors" element={<ProtectedRoute requiredModule="dsgvo"><DsgvoVendorsPage /></ProtectedRoute>} />
                <Route path="dsgvo/avv" element={<ProtectedRoute requiredModule="dsgvo"><DsgvoAVVPage /></ProtectedRoute>} />
                <Route path="dsgvo/vvt" element={<ProtectedRoute requiredModule="dsgvo"><DsgvoVVTPage /></ProtectedRoute>} />
                <Route path="dsgvo/incidents" element={<ProtectedRoute requiredModule="dsgvo"><DsgvoIncidentsPage /></ProtectedRoute>} />
                <Route path="dsgvo/dsr" element={<ProtectedRoute requiredModule="dsgvo"><DsgvoDSRPage /></ProtectedRoute>} />
                <Route path="dsgvo/documents" element={<ProtectedRoute requiredModule="dsgvo"><DsgvoDocumentsPage /></ProtectedRoute>} />
                <Route path="dsgvo/tasks" element={<ProtectedRoute requiredModule="dsgvo"><DsgvoTasksPage /></ProtectedRoute>} />

                {/* Rechnungen */}
                <Route path="rechnungen" element={<ProtectedRoute permission="rechnungen:read" requiredModule="rechnungen"><RechnungenDashboard /></ProtectedRoute>} />
                <Route path="rechnungen/invoices" element={<ProtectedRoute permission="rechnungen:read" requiredModule="rechnungen"><InvoicesPage /></ProtectedRoute>} />
                <Route path="rechnungen/invoices/:id" element={<ProtectedRoute permission="rechnungen:read" requiredModule="rechnungen"><InvoiceDetailPage /></ProtectedRoute>} />
                <Route path="rechnungen/belege" element={<ProtectedRoute permission="rechnungen:read" requiredModule="rechnungen"><InvoicesPage /></ProtectedRoute>} />
                <Route path="rechnungen/payment-runs" element={<ProtectedRoute permission="rechnungen:payment_runs" requiredModule="rechnungen"><PaymentRunsPage /></ProtectedRoute>} />
                <Route path="rechnungen/payment-runs/:id" element={<ProtectedRoute permission="rechnungen:payment_runs" requiredModule="rechnungen"><PaymentRunDetailPage /></ProtectedRoute>} />
                <Route path="rechnungen/ocr" element={<ProtectedRoute permission="rechnungen:write" requiredModule="rechnungen"><OCRImportPage /></ProtectedRoute>} />
                <Route path="rechnungen/liquidity" element={<ProtectedRoute permission="rechnungen:read" requiredModule="rechnungen"><LiquidityPage /></ProtectedRoute>} />
                <Route path="rechnungen/outgoing" element={<ProtectedRoute permission="rechnungen:read" requiredModule="rechnungen"><OutgoingInvoicesPage /></ProtectedRoute>} />
                <Route path="rechnungen/outgoing/:id" element={<ProtectedRoute permission="rechnungen:read" requiredModule="rechnungen"><OutgoingInvoiceDetailPage /></ProtectedRoute>} />
                <Route path="rechnungen/banking" element={<ProtectedRoute strictRoles={["chef"]} requiredModule="rechnungen"><BankingPage /></ProtectedRoute>} />

                {/* HR / Personalverwaltung — alle Mitarbeiter */}
                <Route path="hr/my" element={<ProtectedRoute requiredModule="hr"><MyHRPage /></ProtectedRoute>} />
                <Route path="hr/time-tracking" element={<ProtectedRoute roles={["hr_teamlead", "hr_manager"]} requiredModule="hr"><TimeTrackingPage /></ProtectedRoute>} />
                <Route path="hr/project-time" element={<ProtectedRoute permission="hr_project:read" requiredModule="hr"><ProjectTimePage /></ProtectedRoute>} />
                <Route path="hr/projects" element={<ProtectedRoute permission="hr_project:read" requiredModule="hr"><ProjectsPage /></ProtectedRoute>} />
                <Route path="hr/vacation" element={<ProtectedRoute roles={["hr_teamlead", "hr_manager"]} requiredModule="hr"><VacationPage /></ProtectedRoute>} />
                <Route path="hr/sick-leave" element={<ProtectedRoute roles={["hr_teamlead", "hr_manager"]} requiredModule="hr"><SickLeavePage /></ProtectedRoute>} />
                <Route path="hr/payouts" element={<ProtectedRoute roles={["hr_teamlead", "hr_manager"]} requiredModule="hr"><PayoutsPage /></ProtectedRoute>} />

                {/* HR — Teamleiter + Manager */}
                <Route path="hr/team-overview" element={
                  <ProtectedRoute roles={["hr_teamlead", "hr_manager"]} requiredModule="hr">
                    <TeamOverviewPage />
                  </ProtectedRoute>
                } />

                {/* HR — nur Manager */}
                <Route path="hr" element={
                  <ProtectedRoute roles={["hr_manager"]} requiredModule="hr">
                    <HRDashboard />
                  </ProtectedRoute>
                } />
                <Route path="hr/employees" element={
                  <ProtectedRoute roles={["hr_manager"]} requiredModule="hr">
                    <EmployeesPage />
                  </ProtectedRoute>
                } />
                <Route path="hr/employees/:id/calendar" element={
                  <ProtectedRoute roles={["hr_manager"]} requiredModule="hr">
                    <EmployeeCalendarPage />
                  </ProtectedRoute>
                } />
                <Route path="hr/calendar" element={
                  <ProtectedRoute roles={["hr_manager"]} requiredModule="hr">
                    <CalendarPage />
                  </ProtectedRoute>
                } />
                <Route path="hr/work-time-models" element={
                  <ProtectedRoute roles={["hr_manager"]} requiredModule="hr">
                    <WorkTimeModelsPage />
                  </ProtectedRoute>
                } />
                <Route path="hr/journal" element={
                  <ProtectedRoute roles={["hr_manager"]} requiredModule="hr">
                    <HRJournalPage />
                  </ProtectedRoute>
                } />

                {/* Tickets */}
                <Route path="tickets" element={<ProtectedRoute requiredModule="tickets"><TicketsDashboard /></ProtectedRoute>} />
                <Route path="tickets/:id" element={<ProtectedRoute requiredModule="tickets"><TicketDetailPage /></ProtectedRoute>} />

                {/* Aufgaben (Forgejo-Issue-Boards) */}
                <Route path="aufgaben" element={<ProtectedRoute><TaskBoardsPage /></ProtectedRoute>} />
                <Route path="aufgaben/:boardId" element={<ProtectedRoute><TaskBoardPage /></ProtectedRoute>} />
                <Route path="tester-meldungen" element={<ProtectedRoute><TesterMeldungenPage /></ProtectedRoute>} />

                {/* Admin - nur für Admins */}
                <Route path="admin/testerwerk" element={
                  <ProtectedRoute role="admin">
                    <TesterwerkPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/users" element={
                  <ProtectedRoute role="admin">
                    <UsersListPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/email" element={
                  <ProtectedRoute role="admin">
                    <EmailSettingsPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/settings" element={
                  <ProtectedRoute role="admin">
                    <SettingsPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/matrix" element={
                  <ProtectedRoute role="admin" requiredModule="matrix">
                    <MatrixAdminPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/mattermost" element={
                  <ProtectedRoute role="admin" requiredModule="mattermost">
                    <MattermostAdminPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/pages" element={
                  <ProtectedRoute role="admin">
                    <PagesPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/user-groups" element={
                  <ProtectedRoute role="admin">
                    <UserGroupsPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/ticket-groups" element={
                  <ProtectedRoute role="admin">
                    <TicketGroupsPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/master-data" element={
                  <ProtectedRoute role="admin">
                    <MasterDataPage />
                  </ProtectedRoute>
                } />

                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              </Routes>
            </SetupGate>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
      <Toaster />
      <SonnerToaster richColors position="top-right" />
    </OidcAuthProvider>
  )
}

export default App
